import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronLeft, Plus, Trash2 } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import { api } from '../utils/api.js';
import { locationHooks, meHooks } from '../hooks/useMasters.js';
import Button from '../components/ui/Button.jsx';
import Input from '../components/ui/Input.jsx';
import Select from '../components/ui/Select.jsx';
import Card from '../components/ui/Card.jsx';

const schema = z.object({
  locationId: z.string().min(1, 'Required'),
  fromParty: z.string().min(1, 'Required'),
  toParty: z.string().min(1, 'Required'),
  items: z.array(z.object({ description: z.string().min(1), quantity: z.coerce.number().optional(), unit: z.string().optional() })).min(1, 'Add at least one item'),
});

export default function ITRFormPage() {
  const navigate = useNavigate();
  const { data: locations = [] } = locationHooks.useList();
  const { data: mes = [] } = meHooks.useList();
  const { register, handleSubmit, control, formState: { errors, isSubmitting } } = useForm({
    resolver: zodResolver(schema), defaultValues: { items: [{ description: '', quantity: '', unit: 'kg' }] },
  });
  const { fields, append, remove } = useFieldArray({ control, name: 'items' });
  const createMut = useMutation({ mutationFn: d => api.post('/itrs', d).then(r => r.data.data) });

  const onSubmit = async (values) => {
    const itr = await createMut.mutateAsync(values);
    navigate(`/itrs`);
  };

  const parties = ['Sreeragam Exports Pvt. Ltd.', ...mes.map(m => m.name)];

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 max-w-2xl mx-auto">
      <div className="flex items-center gap-3">
        <Link to="/itrs" className="text-gray-400 hover:text-gray-600"><ChevronLeft className="w-5 h-5" /></Link>
        <h1 className="text-xl font-bold text-gray-900">New Internal Transfer Record</h1>
      </div>
      <Card title="Transfer Details">
        <div className="p-6 space-y-4">
          <Select label="Location" required error={errors.locationId?.message} {...register('locationId')}>
            <option value="">Select location…</option>
            {locations.map(l => <option key={l.id} value={l.id}>{l.code} — {l.name}</option>)}
          </Select>
          <div className="grid grid-cols-2 gap-4">
            <Select label="From Party" required error={errors.fromParty?.message} {...register('fromParty')}>
              <option value="">Select…</option>
              {parties.map(p => <option key={p} value={p}>{p}</option>)}
            </Select>
            <Select label="To Party" required error={errors.toParty?.message} {...register('toParty')}>
              <option value="">Select…</option>
              {parties.map(p => <option key={p} value={p}>{p}</option>)}
            </Select>
          </div>
        </div>
      </Card>
      <Card title="Items" action={<Button type="button" variant="ghost" size="sm" icon={Plus} onClick={() => append({ description:'', quantity:'', unit:'kg' })}>Add Row</Button>}>
        <div className="p-4 space-y-2">
          {fields.map((field, idx) => (
            <div key={field.id} className="flex gap-2 items-start">
              <Input placeholder="Description" {...register(`items.${idx}.description`)} />
              <input type="number" className="w-24 rounded-lg border border-gray-300 px-3 py-2 text-sm" placeholder="Qty" {...register(`items.${idx}.quantity`)} />
              <input className="w-20 rounded-lg border border-gray-300 px-3 py-2 text-sm" placeholder="Unit" {...register(`items.${idx}.unit`)} />
              {fields.length > 1 && <button type="button" onClick={() => remove(idx)} className="mt-2 text-gray-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>}
            </div>
          ))}
        </div>
      </Card>
      <div className="flex justify-end gap-3 pb-6">
        <Link to="/itrs"><Button variant="secondary" type="button">Cancel</Button></Link>
        <Button type="submit" loading={isSubmitting}>Create ITR</Button>
      </div>
    </form>
  );
}
